import { lazy } from "react";

const CHUNK_RELOAD_KEY = "curator:chunkReloaded";
const CHUNK_RELOAD_PARAM = "_cr";

/** True when a lazy route chunk is missing (usually after a deploy). */
export function isChunkLoadFailure(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const message = String(err.message || err);
  return (
    name === "ChunkLoadError"
    || /Loading chunk \d+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message)
    || /error loading dynamically imported module/i.test(message)
  );
}

/** True for script parse errors when Vercel serves index.html for a missing chunk. */
export function isChunkSyntaxError(event) {
  if (!event) return false;
  const message = String(event.message || "");
  const source = String(event.filename || event.target?.src || "");
  return (
    /Unexpected token '<'/i.test(message)
    && /\/static\/js\/.*\.chunk\.js/i.test(source)
  );
}

export function clearChunkReloadGuard() {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Force one hard reload after a deploy so the browser picks up new chunk hashes.
 * Returns true when a reload was triggered.
 */
export function attemptChunkReload() {
  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
  } catch (_e) {
    /* ignore */
  }

  if (alreadyReloaded) return false;

  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch (_e) {
    /* ignore */
  }

  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

/** Call once at startup — clears a stale guard unless we just auto-reloaded. */
export function initChunkReloadGuard() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(CHUNK_RELOAD_PARAM)) {
      url.searchParams.delete(CHUNK_RELOAD_PARAM);
      const qs = url.searchParams.toString();
      const next = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`;
      window.history.replaceState({}, "", next);
      return;
    }
    clearChunkReloadGuard();
  } catch (_e) {
    /* ignore */
  }
}

/** Global listeners catch webpack chunk failures that bypass React.lazy().catch(). */
export function installChunkReloadHandlers() {
  if (typeof window === "undefined" || window.__curatorChunkHandlersInstalled) return;
  window.__curatorChunkHandlersInstalled = true;

  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadFailure(event.reason)) return;
    if (attemptChunkReload()) {
      event.preventDefault();
    }
  });

  window.addEventListener("error", (event) => {
    if (!isChunkSyntaxError(event)) return;
    if (attemptChunkReload()) {
      event.preventDefault();
    }
  });
}

export function lazyWithReload(factory) {
  return lazy(() =>
    factory()
      .then((mod) => {
        clearChunkReloadGuard();
        return mod;
      })
      .catch((err) => {
        if (isChunkLoadFailure(err) && attemptChunkReload()) {
          return new Promise(() => {});
        }
        throw err;
      })
  );
}
